package one.devsbox.www.ui.home

import androidx.compose.animation.Crossfade
import androidx.compose.animation.core.spring
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import one.devsbox.www.io.Repository
import androidx.compose.ui.platform.LocalWindowInfo
import one.devsbox.www.provider.LocalDisplayProvider
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.DataState
import kotlin.time.Duration.Companion.milliseconds

@Composable
@ComposableNavNode
fun HomeUI() {
    with(HomeCtx.get()) {
        when (vm.state) {
            is DataState.Idle -> Unit
            is DataState.Loading -> {
                FullscreenLoadingUI()
            }

            is DataState.Error -> {
                FullscreenErrorUI(
                    message = "Failed to load user profile info",
                    onBack = { Repository.logout() },
                    onReload = { vm.load() }
                )
            }

            is DataState.Result -> SuccessUI(Modifier.fillMaxSize())
        }
    }
}


@Composable
context(ctx: HomeCtx)
private fun LeftPanel() {
    val dp = LocalDisplayProvider.current.state
    val isCollapsed = dp.mobile || dp.mediumScreen

    var showLogoutDialog by remember { mutableStateOf(false) }
    var showSwitchAdminDialog by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    if (showLogoutDialog) {
        AlertDialog(
            onDismissRequest = { showLogoutDialog = false },
            confirmButton = {
                TextButton(onClick = {
                    showLogoutDialog = false
                    Repository.logout()
                }) {
                    Text(stringResource(Res.string.ui_logout_dialog_btn_confirm))
                }
            },
            dismissButton = {
                TextButton(onClick = { showLogoutDialog = false }) {
                    Text(stringResource(Res.string.ui_logout_dialog_btn_dismiss))
                }
            },
            title = {
                Text(
                    text = stringResource(Res.string.ui_logout_dialog_title),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
            },
            text = {
                Text(
                    text = stringResource(Res.string.ui_logout_dialog_message),
                    style = MaterialTheme.typography.bodyMedium
                )
            }
        )
    }

    if (showSwitchAdminDialog) {
        AlertDialog(
            onDismissRequest = { showSwitchAdminDialog = false },
            confirmButton = {
                TextButton(onClick = {
                    showSwitchAdminDialog = false
                    scope.launch {
                        runCatching {
                            Repository.ads.user.switchCompany(ctx.me.info.company.primary)
                        }.onSuccess {
                            kotlinx.browser.window.location.reload()
                        }
                    }
                }) {
                    Text(stringResource(Res.string.yes))
                }
            },
            dismissButton = {
                TextButton(onClick = { showSwitchAdminDialog = false }) {
                    Text(stringResource(Res.string.no))
                }
            },
            title = {
                Text(
                    text = "Switch back to admin mode?",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
            }
        )
    }

    val showUpNavButton = remember {
        mutableStateOf(false)
    }
    LaunchedEffect(Unit) {
        delay(100.milliseconds)
        ctx.nav.findChildNavigation(Graph.HomeContent)?.apply {
            currentNode.collectLatest {
                showUpNavButton.value = backStack.size() > 0
            }
        }
    }
    ModalDrawerSheet(
        drawerContainerColor = MaterialTheme.colorScheme.background,
        modifier = Modifier.width(if (isCollapsed) 48.dp else 200.dp)
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxHeight()) {
            val menuCount = ctx.vm.menuItems.size
            val requiredHeight = if (isCollapsed) {
                212.dp + (menuCount * 48).dp
            } else {
                176.dp + (menuCount * 56).dp
            }

            if (maxHeight < requiredHeight) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                ) {
                    Spacer(Modifier.height(24.dp))
                    LeftPanelLogo(isCollapsed, showUpNavButton.value, ctx)
                    LeftPanelMenuItems(isCollapsed, ctx)
                    if (ctx.me.info.workMode) {
                        WorkModeChip(
                            isCollapsed,
                            ctx,
                            onSwitchClick = { showSwitchAdminDialog = true })
                    }
                    LeftPanelBottomActions(
                        isCollapsed,
                        ctx,
                        onLogoutClick = { showLogoutDialog = true })
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxHeight()
                ) {
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth()
                            .verticalScroll(rememberScrollState())
                    ) {
                        Spacer(Modifier.height(24.dp))
                        LeftPanelLogo(isCollapsed, showUpNavButton.value, ctx)
                        LeftPanelMenuItems(isCollapsed, ctx)
                    }
                    if (ctx.me.info.workMode) {
                        WorkModeChip(
                            isCollapsed,
                            ctx,
                            onSwitchClick = { showSwitchAdminDialog = true })
                    }
                    LeftPanelBottomActions(
                        isCollapsed,
                        ctx,
                        onLogoutClick = { showLogoutDialog = true })
                }
            }
        }
    }
}

@Composable
private fun LeftPanelLogo(
    isCollapsed: Boolean,
    showUpNavButton: Boolean,
    ctx: HomeCtx
) {
    if (isCollapsed) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 24.dp),
            contentAlignment = Alignment.Center
        ) {
            Crossfade(
                targetState = showUpNavButton,
                animationSpec = spring(),
                modifier = Modifier.size(36.dp)
            ) { navOn ->
                if (navOn) {
                    IconButton(
                        onClick = { ctx.nav.findChildNavigation(Graph.HomeContent)?.up() }
                    ) {
                        Icon(
                            imageVector = Svg.ArrowLeft,
                            contentDescription = "up nav",
                            modifier = Modifier.size(18.dp)
                        )
                    }
                } else {
                    Icon(
                        imageVector = Svg.Bsoft,
                        contentDescription = "LOGO",
                        modifier = Modifier.fillMaxSize().padding(2.dp)
                    )
                }
            }
        }
    } else {
        Row(
            modifier = Modifier.padding(bottom = 24.dp, start = 16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Crossfade(
                targetState = showUpNavButton,
                animationSpec = spring(),
                modifier = Modifier.size(48.dp)
            ) { navOn ->
                Box(Modifier.fillMaxSize()) {
                    if (navOn) {
                        IconButton(
                            onClick = { ctx.nav.findChildNavigation(Graph.HomeContent)?.up() },
                            modifier = Modifier.align(Alignment.Center)
                        ) {
                            Icon(Svg.ArrowLeft, "up nav")
                        }
                    } else {
                        Icon(
                            imageVector = Svg.Bsoft,
                            contentDescription = "LOGO",
                            modifier = Modifier.fillMaxSize()
                        )
                    }
                }
            }

            Column(
                modifier = Modifier.padding(start = 8.dp),
            ) {
                Text(
                    text = stringResource(Res.string.app_name),
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 1.sp
                )
                Text(
                    text = stringResource(Res.string.moto).uppercase(),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Light,
                    fontSize = 10.sp,
                    color = MaterialTheme.colorScheme.primary.copy(alpha = 0.7f)
                )
            }
        }
    }
}

@Composable
private fun WorkModeChip(
    isCollapsed: Boolean,
    ctx: HomeCtx,
    onSwitchClick: () -> Unit
) {
    if (isCollapsed) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 12.dp, top = 8.dp),
            contentAlignment = Alignment.Center
        ) {
            OutlinedButton(
                onClick = onSwitchClick,
                modifier = Modifier.size(36.dp),
                shape = CircleShape,
                contentPadding = PaddingValues(0.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.error,
                    containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.1f)
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.error)
            ) {
                Icon(
                    imageVector = Svg.ShieldAccountVariantOutline,
                    contentDescription = "Work Mode",
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    } else {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            contentAlignment = Alignment.Center
        ) {
            OutlinedButton(
                onClick = onSwitchClick,
                shape = RoundedCornerShape(16.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.error,
                    containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.1f)
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.error),
                modifier = Modifier.fillMaxWidth().height(36.dp)
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    Icon(
                        imageVector = Svg.ShieldAccountVariantOutline,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(Modifier.width(8.dp))
                    Text(
                        text = ctx.me.info.company.workingName.uppercase(),
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1
                    )
                }
            }
        }
    }
}

@Composable
private fun LeftPanelMenuItems(
    isCollapsed: Boolean,
    ctx: HomeCtx
) {
    ctx.vm.menuItems.forEach { entry ->
        val selected = entry == ctx.vm.active
        if (isCollapsed) {
            Box(
                modifier = Modifier
                    .padding(horizontal = 6.dp, vertical = 6.dp)
                    .size(36.dp)
                    .border(
                        width = 0.5.dp,
                        color = if (selected) MaterialTheme.colorScheme.secondary else Color.Transparent,
                        shape = CircleShape
                    )
                    .background(
                        color = if (selected) MaterialTheme.colorScheme.secondaryContainer.copy(
                            alpha = 0.05F
                        ) else Color.Transparent,
                        shape = CircleShape
                    ),
                contentAlignment = Alignment.Center
            ) {
                IconButton(
                    onClick = { ctx.action.navTo(entry) },
                    modifier = Modifier.fillMaxSize()
                ) {
                    Icon(
                        imageVector = entry.icon,
                        contentDescription = entry.label.key,
                        tint = if (selected) MaterialTheme.colorScheme.secondary else MaterialTheme.colorScheme.onBackground,
                        modifier = Modifier.size(18.dp)
                    )
                }
            }
        } else {
            NavigationDrawerItem(
                label = { Text(stringResource(entry.label).uppercase()) },
                icon = { Icon(entry.icon, entry.label.key) },
                selected = selected,
                onClick = { ctx.action.navTo(entry) },
                shape = MaterialTheme.shapes.large,
                colors = NavigationDrawerItemDefaults.colors(
                    selectedContainerColor = MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.05F),
                    selectedTextColor = MaterialTheme.colorScheme.secondary,
                    selectedIconColor = MaterialTheme.colorScheme.secondary,
                    unselectedTextColor = MaterialTheme.colorScheme.onBackground
                ),
                modifier = Modifier
                    .padding(NavigationDrawerItemDefaults.ItemPadding)
                    .border(
                        width = 0.5.dp,
                        color = if (!selected) Color.Transparent else MaterialTheme.colorScheme.secondary,
                        shape = MaterialTheme.shapes.large
                    )
            )
        }
    }
}

@Composable
private fun LeftPanelBottomActions(
    isCollapsed: Boolean,
    ctx: HomeCtx,
    onLogoutClick: () -> Unit
) {
    if (isCollapsed) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 24.dp, top = 16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            OutlinedButton(
                onClick = { ctx.prefs.systemDark = !ctx.prefs.systemDark },
                modifier = Modifier.size(36.dp),
                shape = CircleShape,
                contentPadding = PaddingValues(0.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.onBackground
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.onBackground)
            ) {
                Icon(
                    imageVector = if (ctx.prefs.systemDark) Svg.Sun else Svg.Moon,
                    contentDescription = "Theme Switch",
                    modifier = Modifier.size(18.dp)
                )
            }

            OutlinedButton(
                onClick = onLogoutClick,
                modifier = Modifier.size(36.dp),
                shape = CircleShape,
                contentPadding = PaddingValues(0.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.onBackground
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.onBackground)
            ) {
                Icon(
                    imageVector = Svg.LogoutOutline,
                    contentDescription = "LOGOUT",
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    } else {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp)
                .padding(bottom = 24.dp, top = 16.dp),
            horizontalArrangement = Arrangement.SpaceEvenly,
            verticalAlignment = Alignment.CenterVertically
        ) {
            OutlinedButton(
                onClick = { ctx.prefs.systemDark = !ctx.prefs.systemDark },
                modifier = Modifier.size(40.dp),
                shape = CircleShape,
                contentPadding = PaddingValues(0.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.onBackground
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.onBackground)
            ) {
                Icon(
                    imageVector = if (ctx.prefs.systemDark) Svg.Sun else Svg.Moon,
                    contentDescription = "Theme Switch",
                    modifier = Modifier.size(20.dp)
                )
            }

            OutlinedButton(
                onClick = onLogoutClick,
                modifier = Modifier.size(40.dp),
                shape = CircleShape,
                contentPadding = PaddingValues(0.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.onBackground
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.onBackground)
            ) {
                Icon(
                    imageVector = Svg.LogoutOutline,
                    contentDescription = "LOGOUT",
                    modifier = Modifier.size(20.dp)
                )
            }
        }
    }
}

@Composable
context(ctx: HomeCtx)
private fun SuccessUI(modifier: Modifier = Modifier) {

    Row(modifier) {
        LeftPanel()
        VerticalDivider()
        Box(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight()
        ) {
            NavigationChildHomeContent(Node.EmptyUI)
        }
    }

    LaunchedEffect(ctx.vm.active) {
        delay(50.milliseconds)
        if (ctx.vm.active == Node.EmptyUI) {
            ctx.action.navTo(ctx.vm.menuItems.first())
        }
    }

}

val Node.label: StringResource
    get() = when (this) {
        Node.ListingLocationUI -> Res.string.locations
        Node.ListingDevicesUI -> Res.string.devices
        Node.ListingUsersUI -> Res.string.users
        Node.ListingCampaignsUI -> Res.string.campaigns
        Node.ListingResourcesUI -> Res.string.media
        Node.ListingCompanyUI -> Res.string.companies
        else -> Res.string.empty
    }

val Node.icon: ImageVector
    get() = when (this) {
        Node.ListingLocationUI -> Svg.LocationOutline
        Node.ListingDevicesUI -> Svg.DeviceOutline
        Node.ListingUsersUI -> Svg.UserOutline
        Node.ListingCampaignsUI -> Svg.CampaignOutline
        Node.ListingResourcesUI -> Svg.MediaOutline
        Node.ListingCompanyUI -> Svg.ListBoxOutline
        else -> Svg.ArrowLeft
    }